import { useMemo } from "react";
import { Box, Code, Flex, Grid, GridItem, Tag, Text, VStack, Wrap, WrapItem } from "@chakra-ui/react";
import { MdFilterAlt, MdInfoOutline, MdMarkEmailUnread } from "react-icons/md";
import {
  CANAL_LABEL,
  CatalogoDisparo,
  CombinadorFiltro,
  ContextoDisparo,
  DESTINATARIO_LABEL,
  DisparoConfiguracao,
  FORMATO_LABEL,
  MODO_TEMPLATE_LABEL,
  ModoTemplate,
  OperadorFiltro,
  PROVEDOR_LABEL,
  TIPO_GATILHO_LABEL,
  TipoGatilho,
  descreverAgendamento,
  formatDateBr,
  rotuloContexto,
  rotuloOperador,
  tokenVariavel,
} from "@/models/consignado/disparo.model";
import {
  evento as buscarEvento,
  propriedade as buscarPropriedade,
  rotuloDoValor,
} from "./catalogo.helpers";
import { descreverDataRelativa, ehDataRelativa } from "./data-relativa";
import {
  COMBINADOR_LABEL,
  FiltroNo,
  GrupoNo,
  arvoreDeFiltrosApi,
  condicoesDaArvore,
} from "./filtro-arvore";
import SectionCard from "./SectionCard";
import { fieldFont, labelStyles, valueStyles } from "./styleTokens";

type Props = {
  disparo: DisparoConfiguracao;
  catalogo?: CatalogoDisparo;
};

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box>
      <Text {...labelStyles} textTransform="uppercase" letterSpacing="0.03em" fontSize="xs">
        {label}
      </Text>
      <Box {...valueStyles} mt={0.5}>
        {children}
      </Box>
    </Box>
  );
}

/**
 * Mesma árvore do formulário, só que em leitura: condição vira tag e
 * subgrupo vira uma caixa tracejada — o parêntese, que é o que diz se
 * "A E B OU C" quer dizer uma coisa ou outra.
 */
function FiltroNoResumo({
  no,
  contexto,
  catalogo,
  combinadorDoPai,
  primeiro,
}: {
  no: FiltroNo;
  contexto: ContextoDisparo;
  catalogo?: CatalogoDisparo;
  combinadorDoPai?: CombinadorFiltro;
  primeiro: boolean;
}) {
  const conector = !primeiro && combinadorDoPai && (
    <Tag size="sm" bg="gray.200" color="gray.700" borderRadius="md" fontWeight="700" {...fieldFont}>
      {combinadorDoPai}
    </Tag>
  );

  if (no.tipoNo === "condicao") {
    const p = buscarPropriedade(catalogo, contexto, no.propriedade);
    return (
      <>
        {conector}
        <Tag colorScheme="blue" borderRadius="md" {...fieldFont}>
          {p?.descricao ?? no.propriedade} · {rotuloOperador(no.operador, p?.tipo)} ·{" "}
          {no.valores
            .map((v) =>
              ehDataRelativa(v) ? descreverDataRelativa(v) : rotuloDoValor(contexto, no.propriedade, v),
            )
            .join(no.operador === OperadorFiltro.ENTRE ? " – " : ", ")}
        </Tag>
      </>
    );
  }

  return (
    <>
      {conector}
      <Flex
        align="center"
        gap={2}
        wrap="wrap"
        borderWidth="1px"
        borderStyle="dashed"
        borderColor="secondary"
        borderRadius="md"
        p={1.5}
      >
        <FiltroGrupoResumo grupo={no} contexto={contexto} catalogo={catalogo} />
      </Flex>
    </>
  );
}

function FiltroGrupoResumo({
  grupo,
  contexto,
  catalogo,
}: {
  grupo: GrupoNo;
  contexto: ContextoDisparo;
  catalogo?: CatalogoDisparo;
}) {
  return (
    <>
      {grupo.itens.map((no, i) => (
        <FiltroNoResumo
          key={no.id}
          no={no}
          contexto={contexto}
          catalogo={catalogo}
          combinadorDoPai={grupo.combinador}
          primeiro={i === 0}
        />
      ))}
    </>
  );
}

/** Corpo em HTML é mostrado como texto — é o cadastro, não a prévia do e-mail. */
const semTags = (html: string): string => html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

export default function DisparoView({ disparo, catalogo }: Props) {
  const ev = buscarEvento(catalogo, disparo.gatilho.evento);
  // O GET devolve a árvore gravada (ou a lista plana de um cadastro antigo);
  // normalizar aqui é o que deixa as duas formas abrirem. Memoizado porque a
  // conversão sorteia os ids de UI — sem isso, cada render trocaria as keys.
  const filtros = useMemo(() => arvoreDeFiltrosApi(disparo.filtros), [disparo.filtros]);
  const condicoes = condicoesDaArvore(filtros);

  return (
    <VStack spacing={5} align="stretch">
      <Flex align="center" gap={2} wrap="wrap">
        <Tag colorScheme={disparo.ativo ? "green" : "gray"} borderRadius="md" {...fieldFont}>
          {disparo.ativo ? "Ativo" : "Inativo"}
        </Tag>
        <Tag colorScheme="blue" borderRadius="md" {...fieldFont}>
          {TIPO_GATILHO_LABEL[disparo.gatilho.tipo]}
        </Tag>
        <Tag colorScheme="gray" borderRadius="md" {...fieldFont}>
          {rotuloContexto(disparo.contexto, catalogo)}
        </Tag>
        <Tag colorScheme="purple" borderRadius="md" {...fieldFont}>
          versão {disparo.versao}
        </Tag>
      </Flex>

      <SectionCard title="Identificação do disparo" icon={MdInfoOutline}>
        <VStack spacing={4} align="stretch">
          <Field label="Nome">{disparo.nome}</Field>

          <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={4}>
            <GridItem>
              <Field label="Contexto">{rotuloContexto(disparo.contexto, catalogo)}</Field>
            </GridItem>
            <GridItem>
              <Field label="Destinatário">{DESTINATARIO_LABEL[disparo.destinatario]}</Field>
            </GridItem>
            <GridItem>
              <Field label="Gatilho">{TIPO_GATILHO_LABEL[disparo.gatilho.tipo]}</Field>
            </GridItem>
          </Grid>

          {disparo.gatilho.tipo === TipoGatilho.EVENTO && (
            <Field label="Evento">
              <Text {...fieldFont}>{ev?.descricao ?? disparo.gatilho.evento ?? "—"}</Text>
              {ev && (
                <Flex gap={2} mt={1} wrap="wrap">
                  <Tag size="sm" colorScheme="gray" {...fieldFont}>
                    origem {ev.origem}
                  </Tag>
                  {ev.codigoExterno !== null && (
                    <Tag size="sm" colorScheme="gray" {...fieldFont}>
                      código {ev.codigoExterno}
                    </Tag>
                  )}
                  {ev.definicaoPendente && (
                    <Tag size="sm" colorScheme="orange" {...fieldFont}>
                      definição pendente
                    </Tag>
                  )}
                </Flex>
              )}
            </Field>
          )}

          {disparo.gatilho.tipo === TipoGatilho.ROTINA && (
            <Field label="Agendamento">
              <Text {...fieldFont}>{descreverAgendamento(disparo.gatilho.agendamento)}</Text>
              <Text fontSize="xs" color="gray.500" {...fieldFont}>
                cron: {disparo.expressaoCron || "—"} · {disparo.gatilho.agendamento?.timezone ?? "—"}
              </Text>
            </Field>
          )}
        </VStack>
      </SectionCard>

      {/* Evento não usa filtro: a seção só aparece nele se houver um gravado
          (de quando o evento ainda filtrava), avisando que sai ao salvar. */}
      {(disparo.gatilho.tipo !== TipoGatilho.EVENTO || condicoes.length > 0) && (
        <SectionCard title="Filtro" icon={MdFilterAlt}>
          {disparo.gatilho.tipo === TipoGatilho.EVENTO && (
            <Text fontSize="xs" color="orange.600" mb={2} {...fieldFont}>
              Disparo por evento não usa filtro. Este foi gravado antes e sai na próxima vez que o
              disparo for salvo.
            </Text>
          )}
          {condicoes.length === 0 ? (
            <Text fontSize="sm" color="gray.500" {...fieldFont}>
              Sem filtro — vale para todos os registros do contexto.
            </Text>
          ) : (
            <Flex align="center" gap={2} wrap="wrap">
              <FiltroGrupoResumo grupo={filtros} contexto={disparo.contexto} catalogo={catalogo} />
            </Flex>
          )}
          {condicoes.length > 1 && (
            <Text fontSize="xs" color="gray.500" mt={2} {...fieldFont}>
              {COMBINADOR_LABEL[filtros.combinador]}.
            </Text>
          )}
        </SectionCard>
      )}

      <SectionCard title="Canal e mensagem" icon={MdMarkEmailUnread}>
        <VStack spacing={4} align="stretch">
          {disparo.canais.map((c, i) => {
            const contato = buscarPropriedade(catalogo, disparo.contexto, c.origemContato.propriedade);
            const template = c.template;
            const variaveis = Object.entries(template?.variaveis ?? {});
            return (
              <Box
                key={`${c.canal}-${i}`}
                borderWidth="1px"
                borderColor="gray.200"
                borderRadius="12px"
                p={4}
              >
                <Flex align="center" gap={2} mb={3} wrap="wrap">
                  <Tag colorScheme="green" borderRadius="md" {...fieldFont}>
                    {CANAL_LABEL[c.canal]}
                  </Tag>
                  <Tag colorScheme="gray" borderRadius="md" {...fieldFont}>
                    {PROVEDOR_LABEL[c.provedor]}
                  </Tag>
                  <Tag colorScheme="gray" borderRadius="md" {...fieldFont}>
                    remetente {c.remetente}
                  </Tag>
                  {template && (
                    <Tag colorScheme="blue" borderRadius="md" {...fieldFont}>
                      {MODO_TEMPLATE_LABEL[template.modo]}
                    </Tag>
                  )}
                </Flex>

                <VStack align="stretch" spacing={3}>
                  <Field label="Contato">
                    {contato?.descricao ?? c.origemContato.propriedade}
                    {c.origemContato.fallbacks?.length
                      ? ` (alternativos: ${c.origemContato.fallbacks.join(", ")})`
                      : ""}
                  </Field>

                  {template?.modo === ModoTemplate.CORPO_PROPRIO ? (
                    <>
                      {template.assunto && <Field label="Assunto">{template.assunto}</Field>}
                      <Field label="Mensagem">
                        <Text fontSize="sm" whiteSpace="pre-wrap" {...fieldFont}>
                          {template.tipoConteudo === "HTML"
                            ? semTags(template.conteudo ?? "")
                            : template.conteudo}
                        </Text>
                      </Field>
                    </>
                  ) : template?.referenciaExterna ? (
                    <>
                      <Field label="Template no provedor">
                        {template.referenciaExterna.identificador}
                        {template.referenciaExterna.idioma
                          ? ` · ${template.referenciaExterna.idioma}`
                          : ""}
                        {template.referenciaExterna.produtoId
                          ? ` · produto ${template.referenciaExterna.produtoId}`
                          : ""}
                      </Field>
                      <Field label="Parâmetros">
                        <Code fontSize="xs" display="block" whiteSpace="pre-wrap" p={2} borderRadius="md">
                          {template.referenciaExterna.parametros
                            .map((p) => `${p.componente}[${p.indice}] = ${p.valor}`)
                            .join("\n")}
                        </Code>
                      </Field>
                    </>
                  ) : (
                    <Text fontSize="sm" color="gray.500" {...fieldFont}>
                      Mensagem não configurada.
                    </Text>
                  )}

                  {variaveis.length > 0 && (
                    <Field label="Variáveis">
                      <Wrap>
                        {variaveis.map(([apelido, v]) => {
                          const p = v.propriedade
                            ? buscarPropriedade(catalogo, disparo.contexto, v.propriedade)
                            : undefined;
                          const origem = v.constante !== undefined
                            ? `"${v.constante}"`
                            : p?.descricao ?? v.propriedade ?? "—";
                          return (
                            <WrapItem key={apelido}>
                              <Tag size="sm" colorScheme="gray" borderRadius="md" {...fieldFont}>
                                {tokenVariavel(apelido)} ← {origem} · {FORMATO_LABEL[v.formato]}
                                {v.obrigatoria ? "" : ` · padrão "${v.fallback ?? ""}"`}
                              </Tag>
                            </WrapItem>
                          );
                        })}
                      </Wrap>
                    </Field>
                  )}
                </VStack>
              </Box>
            );
          })}
        </VStack>
      </SectionCard>

      <Flex justify="space-between" color="gray.400" fontSize="xs" {...fieldFont} wrap="wrap" gap={2}>
        <Text>
          Criado em {formatDateBr(disparo.criadoEm)}
          {disparo.criadoPor ? ` por ${disparo.criadoPor}` : ""}
        </Text>
        <Text>
          Atualizado em {formatDateBr(disparo.atualizadoEm)}
          {disparo.atualizadoPor ? ` por ${disparo.atualizadoPor}` : ""}
        </Text>
      </Flex>
    </VStack>
  );
}
